import cross from '@assets/nuevos-iconos/x.png'
import { emptyFileInfo } from '@baseValues'
import { uploadItem } from '@proxies/uploadGLB'
import * as Dialog from '@radix-ui/react-dialog'
import * as Form from '@radix-ui/react-form'
import * as Select from '@radix-ui/react-select'
import { useAppStore } from '@store'
import { FileInfo } from '@types'
import {
  convertBlobToBase64,
  convertToGLB,
  load3DSModel,
  loadDAEModel,
  loadFBXModel,
  loadGLTFModel,
  loadOBJModel,
  loadPLYModel,
  loadSTLModel,
} from '@utils/conversion/fileConverter'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const FileUploader = () => {
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(emptyFileInfo)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const closeModal = useAppStore.use.closeModal()
  const openModal = useAppStore.use.openModal()
  const t = useAppStore.use.t()

  useEffect(() => {
    openModal()
    return () => {
      closeModal()
    }
  }, [])

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]
      const url = URL.createObjectURL(file)
      const extension = file.name.split('.').pop().toLowerCase() as FileInfo['originalExtension']
      const name = file.name.split('.').slice(0, -1).join('.')
      let model

      try {
        switch (extension) {
          case 'gltf':
          case 'glb':
            model = await loadGLTFModel(url)
            break
          case 'stl':
            model = await loadSTLModel(url)
            break
          case '3ds':
            model = await load3DSModel(url)
            break
          case 'dae':
            model = await loadDAEModel(url)
            break
          case 'obj':
            model = await loadOBJModel(url)
            break
          case 'fbx':
            model = await loadFBXModel(url)
            break
          case 'ply':
            model = await loadPLYModel(url)
            break
          default:
            throw new Error('Unsupported file type')
        }

        const glbBlob = await convertToGLB(model)
        const glbBase64 = await convertBlobToBase64(glbBlob)

        setFileInfo({
          ...emptyFileInfo,
          fileName: name,
          fileDescription: 'Base description',
          originalExtension: extension,
          glbBlob,
          newExtension: 'glb',
          unit: 'cm',
          reference: 'Base reference',
          longDescription: 'Base long description',
          tipoNormalizado: 'otros',
          glbBase64,
          folder: 'Favoritos',
          price: 0,
          url: 'base url',
        })

        setError(null)
      } catch (error) {
        console.error('Error processing file:', error)
        setError(error.message)
      }
    }
  }, [])

  const handleModelFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    await onDrop(selectedFiles)
    e.target.value = ''
  }

  const handleInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target
    setFileInfo((prev) => {
      const updatedItem = { ...prev, [name]: value }
      return updatedItem
    })
  }

  const handleSelectChange = (name: string, value: string) => {
    setFileInfo((prev) => {
      const updatedItem = { ...prev, [name]: value }
      return updatedItem
    })
  }

  const handleFileUpload = async () => {
    if (fileInfo) {
      const result = await uploadItem(fileInfo)

      if (result.success) {
        navigate('/planner')
      } else {
        setError(result.message || 'Error desconocido.')
      }
    }
  }

  const handlePngDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const pngFile = acceptedFiles[0]
      const reader = new FileReader()
      reader.onloadend = () => {
        const blob = new Blob([reader.result as ArrayBuffer], { type: 'image/png' })
        setFileInfo((prev) => (prev ? { ...prev, pngBlob: blob } : null))
      }
      reader.readAsArrayBuffer(pngFile)
    }
  }

  const handlePngFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    handlePngDrop(selectedFiles)
    e.target.value = ''
  }

  const handleClose = () => {
    setFileInfo(null)
    navigate('/planner')
  }

  return (
    <Dialog.Root open={true}>
      <Dialog.Overlay className='fixed inset-0 z-10 bg-black opacity-30' />
      <Dialog.Content
        onInteractOutside={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        className='absolute inset-0 left-1/2 top-1/2 z-20 h-min min-h-[250px] w-[680px] -translate-x-1/2 -translate-y-1/2 place-items-center justify-center rounded-[15px] border-2 border-solid border-C/Color3 bg-C/Color1 shadow-xl'
      >
        <Form.Root>
          <div className='mb-16 mt-8 grid w-[650px] grid-cols-2 gap-3 rounded-md border-none bg-C/Color10 p-3 shadow-md'>
            <Dialog.Title className='col-span-2 mb-1 ml-1 border-b-2 border-C/Color3 text-xl font-normal text-C/Color5'>
              {'Cargar archivo'}
            </Dialog.Title>
            <Dialog.Description className='ml-3 text-xs'>{'Por favor, sube tu archivo y proporciona los detalles necesarios.'}</Dialog.Description>
            <label className='col-span-2 h-14 cursor-pointer rounded-md border-2 border-dashed border-gray-300 text-center'>
              <input
                type='file'
                accept='.stl,.glb,.gltf,.3ds,.dae,.obj,.fbx,.ply'
                onChange={handleModelFileChange}
                className='hidden'
              />
              <div className='mt-4'>{!fileInfo ? `${'Arrastra y suelta archivos aquí, o haz clic para seleccionar archivos'}` : `${'item procesado, haz clic para seleccionar otro archivo'}`}</div>
            </label>
            {error && (
              <div className='absolute left-[21rem] top-[13rem] col-span-2 text-[10px] text-C/Color6'>
                {error}
              </div>
            )}
            <Dialog.Close
              onClick={handleClose}
              asChild
            >
              <img
                className='absolute right-2.5 top-2.5 cursor-pointer'
                src={cross}
              />
            </Dialog.Close>
            <Form.Field
              name='fileName'
              className='col-span-1 flex justify-between'
            >
              <Form.Label className=' text-lg text-C/Color5'>{'Nombre del archivo'}:</Form.Label>
              <Form.Control asChild>
                <input
                  type='text'
                  name='fileName'
                  pattern='[a-zá-úà-ùçÇ0-9_\\s-]*'
                  value={fileInfo?.fileName}
                  id='fileName'
                  onChange={(e) => handleInput(e)}
                  className='text-md ml-2 mt-1 h-8 w-[120px] rounded-sm border border-solid border-C/Color4 pl-1 text-C/Color4'
                />
              </Form.Control>
            </Form.Field>
            <Form.Field
              name='width'
              className='col-span-1 flex justify-between'
            >
              <Form.Label className=' text-lg text-C/Color5'>{'Ancho'}:</Form.Label>
              <Form.Control asChild>
                <input
                  type='number'
                  name='width'
                  id='width'
                  value={fileInfo?.width}
                  onChange={(e) => handleInput(e)}
                  className='text-md ml-2 mt-1 h-8 w-[120px] rounded-sm border border-solid border-C/Color4 pl-1 text-C/Color4'
                />
              </Form.Control>
            </Form.Field>
            <Form.Field
              name='reference'
              className='col-span-1 flex justify-between'
            >
              <Form.Label className=' text-lg text-C/Color5'>{'Referencia'}:</Form.Label>
              <Form.Control asChild>
                <input
                  type='text'
                  name='reference'
                  pattern='[a-zá-úà-ùçÇ0-9_\\s-]*'
                  value={fileInfo?.reference}
                  id='reference'
                  onChange={(e) => handleInput(e)}
                  className='text-md ml-2 mt-1 h-8 w-[120px] rounded-sm border border-solid border-C/Color4 pl-1 text-C/Color4'
                />
              </Form.Control>
            </Form.Field>
            <Form.Field
              name='height'
              className='col-span-1 flex justify-between'
            >
              <Form.Label className=' text-lg text-C/Color5'>{'Alto'}:</Form.Label>
              <Form.Control asChild>
                <input
                  type='number'
                  name='height'
                  id='height'
                  value={fileInfo?.height}
                  onChange={(e) => handleInput(e)}
                  className='text-md ml-2 mt-1 h-8 w-[120px] rounded-sm border border-solid border-C/Color4 pl-1 text-C/Color4'
                />
              </Form.Control>
            </Form.Field>
            <Form.Field
              name='fileDescription'
              className='col-span-1 flex justify-between'
            >
              <Form.Label className=' text-lg text-C/Color5'>{'Descripción'}:</Form.Label>
              <Form.Control asChild>
                <input
                  type='text'
                  name='fileDescription'
                  id='fileDescription'
                  value={fileInfo?.fileDescription}
                  onChange={(e) => handleInput(e)}
                  className='text-md ml-2 mt-1 h-8 w-[120px] rounded-sm border border-solid border-C/Color4 pl-1 text-C/Color4'
                />
              </Form.Control>
            </Form.Field>
            <Form.Field
              name='depth'
              className='col-span-1 flex justify-between'
            >
              <Form.Label className=' text-lg text-C/Color5'>{'Fondo'}:</Form.Label>
              <Form.Control asChild>
                <input
                  type='number'
                  name='depth'
                  id='depth'
                  value={fileInfo?.depth}
                  onChange={(e) => handleInput(e)}
                  className='text-md ml-2 mt-1 h-8 w-[120px] rounded-sm border border-solid border-C/Color4 pl-1 text-C/Color4'
                />
              </Form.Control>
            </Form.Field>
            <Form.Field
              name='longDescription'
              className='col-span-2 flex justify-between'
            >
              <Form.Label className=' text-lg text-C/Color5'>{'Descripcion larga'}:</Form.Label>
              <Form.Control asChild>
                <textarea
                  name='longDescription'
                  id='longDescription'
                  value={fileInfo?.longDescription}
                  onChange={(e) => handleInput(e)}
                  className='text-md ml-2 mt-1 h-16 w-[620px] rounded-sm border border-solid border-C/Color4 pl-1 text-C/Color4'
                />
              </Form.Control>
            </Form.Field>
            <Form.Field
              name='url'
              className='col-span-1 flex justify-between'
            >
              <Form.Label className=' text-lg text-C/Color5'>{'Url'}:</Form.Label>
              <Form.Control asChild>
                <input
                  type='text'
                  name='url'
                  id='url'
                  value={fileInfo?.url}
                  onChange={(e) => handleInput(e)}
                  className='text-md ml-2 mt-1 h-8 w-[120px] rounded-sm border border-solid border-C/Color4 pl-1 text-C/Color4'
                />
              </Form.Control>
            </Form.Field>
            <Form.Field
              name='placementHeight'
              className='col-span-1 flex justify-between'
            >
              <Form.Label className=' text-lg text-C/Color5'>{'Altura De Colocación'}</Form.Label>
              <Form.Control asChild>
                <input
                  type='number'
                  name='placementHeight'
                  id='placementHeight'
                  value={fileInfo?.placementHeight}
                  onChange={(e) => handleInput(e)}
                  className='text-md ml-2 mt-1 h-8 w-[120px] rounded-sm border border-solid border-C/Color4 pl-1 text-C/Color4'
                />
              </Form.Control>
            </Form.Field>
            <Form.Field
              name='unit'
              id='unit'
              className='col-span-1 flex justify-between'
            >
              <Form.Label className=' text-lg text-C/Color5'>{'Unidad de medida'}:</Form.Label>
              <Form.Control asChild>
                <Select.Root
                  value={fileInfo?.unit || 'cm'}
                  onValueChange={(value) => handleSelectChange('unit', value)}
                >
                  <Select.Trigger className='text-md ml-2 mt-1 h-8 w-[60px] rounded-sm border border-solid border-C/Color4 pl-1 text-C/Color4'>
                    <Select.Value />
                    <Select.Icon />
                  </Select.Trigger>
                  <Select.Content className='absolute -ml-[552px] mt-[13.5rem] w-[60px] rounded border bg-white shadow-lg'>
                    <Select.Viewport>
                      <Select.Item
                        value='cm'
                        className='p-2 hover:bg-gray-200'
                      >
                        <Select.ItemText>cm</Select.ItemText>
                      </Select.Item>
                      <Select.Item
                        value='m'
                        className='p-2 hover:bg-gray-200'
                      >
                        <Select.ItemText>m</Select.ItemText>
                      </Select.Item>
                      <Select.Item
                        value='mm'
                        className='p-2 hover:bg-gray-200'
                      >
                        <Select.ItemText>mm</Select.ItemText>
                      </Select.Item>
                      <Select.Item
                        value='in'
                        className='p-2 hover:bg-gray-200'
                      >
                        <Select.ItemText>in</Select.ItemText>
                      </Select.Item>
                      <Select.Item
                        value='ft'
                        className='p-2 hover:bg-gray-200'
                      >
                        <Select.ItemText>ft</Select.ItemText>
                      </Select.Item>
                    </Select.Viewport>
                  </Select.Content>
                </Select.Root>
              </Form.Control>
            </Form.Field>
            <Form.Field
              name='tipoNormalizado'
              id='tipoNormalizado'
              className='col-span-1 flex justify-between'
            >
              <Form.Label className=' text-lg text-C/Color5'>{'Tipo normalizado'}:</Form.Label>
              <Form.Control asChild>
                <Select.Root
                  value={fileInfo?.tipoNormalizado || 'otros'}
                  onValueChange={(value) => handleSelectChange('tipoNormalizado', value)}
                >
                  <Select.Trigger className='text-md ml-2 mt-1 h-8 w-[60px] rounded-sm border border-solid border-C/Color4 pl-1 text-C/Color4'>
                    <Select.Value />
                    <Select.Icon />
                  </Select.Trigger>
                  <Select.Content className='absolute -ml-[552px] mt-[13.5rem] w-[60px] rounded border bg-white shadow-lg'>
                    <Select.Viewport>
                      <Select.Item
                        value='MAL'
                        className='p-2 hover:bg-gray-200'
                      >
                        <Select.ItemText>MAL</Select.ItemText>
                      </Select.Item>
                      <Select.Item
                        value='MBJ'
                        className='p-2 hover:bg-gray-200'
                      >
                        <Select.ItemText>MBJ</Select.ItemText>
                      </Select.Item>
                      <Select.Item
                        value='otros'
                        className='p-2 hover:bg-gray-200'
                      >
                        <Select.ItemText>otros</Select.ItemText>
                      </Select.Item>
                    </Select.Viewport>
                  </Select.Content>
                </Select.Root>
              </Form.Control>
            </Form.Field>
            <Form.Field
              name='price'
              className='col-span-1 flex justify-between'
            >
              <Form.Label className='text-lg text-C/Color5'>Precio:</Form.Label>
              <Form.Control asChild>
                <input
                  type='number'
                  name='price'
                  value={fileInfo?.price}
                  onChange={(e) => handleInput(e)}
                  className='text-md ml-2 mt-1 h-8 w-[120px] rounded-sm border border-solid border-C/Color4 pl-1 text-C/Color4'
                />
              </Form.Control>
            </Form.Field>
            <Form.Field
              name='folder'
              className='col-span-1 flex justify-between'
            >
              <Form.Label className='text-lg text-C/Color5'>Carpeta:</Form.Label>
              <Form.Control asChild>
                <input
                  type='text'
                  name='folder'
                  value={fileInfo?.folder}
                  onChange={(e) => handleInput(e)}
                  className='text-md ml-2 mt-1 h-8 w-[120px] rounded-sm border border-solid border-C/Color4 pl-1 text-C/Color4'
                />
              </Form.Control>
            </Form.Field>
            <Form.Field
              name='pngBlob'
              className='col-span-2 flex justify-between'
            >
              <Form.Label className='text-lg text-C/Color5'>Imagen PNG:</Form.Label>
              <Form.Control asChild>
                <label className='col-span-2 h-14 w-[32rem] cursor-pointer rounded-md border-2 border-dashed border-gray-300 text-center'>
                  <input
                    type='file'
                    accept='image/png'
                    onChange={handlePngFileChange}
                    className='hidden'
                  />
                  <div className='mt-4'>{!fileInfo ? `${'Arrastra y suelta archivos aquí, o haz clic para seleccionar archivos'}` : `${'item procesado, haz clic para seleccionar otro archivo'}`}</div>
                </label>
              </Form.Control>
            </Form.Field>
          </div>
        </Form.Root>
        <Dialog.Close asChild>
          <button
            onClick={handleClose}
            className='absolute bottom-4 left-4 mt-4 rounded bg-red-500 p-2 text-white'
          >
            Cancelar
          </button>
        </Dialog.Close>
        {fileInfo && (
          <button
            onClick={handleFileUpload}
            className='absolute bottom-4 right-4 mt-2 inline-block rounded bg-blue-500 px-4 py-2 text-white'
          >
            Upload GLB
          </button>
        )}
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default FileUploader
